import axios from 'axios';
import { secureStorage } from './secureStorage';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const MAX_BREADCRUMBS = 20;
const SLOW_API_THRESHOLD_MS = 3000;
const LARGE_PAYLOAD_BYTES = 1024 * 1024; // 1MB
const LOCAL_STORAGE_KEY = 'jp_debug_queue';
const FLUSH_INTERVAL_MS = 30000; // 30s
const MAX_LOCAL_QUEUE = 80;

let breadcrumbs = [];
let isInitialized = false;
let flushTimer = null;

// ─── Breadcrumb Trail ────────────────────────────────────
function addBreadcrumb(action, detail) {
  breadcrumbs.push({
    action,
    detail: String(detail || '').slice(0, 200),
    ts: new Date().toISOString(),
    url: window.location.pathname
  });
  if (breadcrumbs.length > MAX_BREADCRUMBS) breadcrumbs.shift();
}

// ─── localStorage Queue (Safe Mode) ─────────────────────
function queueToLocal(entry) {
  try {
    const raw = localStorage.getItem(LOCAL_STORAGE_KEY);
    const queue = raw ? JSON.parse(raw) : [];
    queue.push(entry);
    if (queue.length > MAX_LOCAL_QUEUE) queue.splice(0, queue.length - MAX_LOCAL_QUEUE);
    localStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify(queue));
  } catch (e) { if (e?.name !== 'QuotaExceededError') console.warn('[debug] queue write failed:', e?.message); }
}

function flushLocalQueue() {
  try {
    const raw = localStorage.getItem(LOCAL_STORAGE_KEY);
    if (!raw) return;
    const queue = JSON.parse(raw);
    if (!queue.length) return;
    localStorage.removeItem(LOCAL_STORAGE_KEY);
    const token = secureStorage.getToken();
    if (!token) { localStorage.setItem(LOCAL_STORAGE_KEY, raw); return; }
    const batches = [];
    for (let i = 0; i < queue.length; i += 10) {
      batches.push(queue.slice(i, i + 10));
    }
    batches.forEach(batch => {
      navigator.sendBeacon?.(
        `${API}/debug/log-batch`,
        new Blob([JSON.stringify({ logs: batch, token })], { type: 'application/json' })
      ) || axios.post(`${API}/debug/log-batch`, { logs: batch }, {
        headers: { Authorization: `Bearer ${token}` },
        timeout: 5000
      }).catch(() => {});
    });
  } catch (e) { console.warn('[debug] flush failed:', e?.message); }
}

// ─── Memory Snapshot ─────────────────────────────────────
function getMemorySnapshot() {
  try {
    const mem = performance?.memory;
    if (mem) {
      return {
        usedMB: Math.round(mem.usedJSHeapSize / 1048576),
        totalMB: Math.round(mem.totalJSHeapSize / 1048576),
        limitMB: Math.round(mem.jsHeapSizeLimit / 1048576),
        pct: Math.round((mem.usedJSHeapSize / mem.jsHeapSizeLimit) * 100)
      };
    }
  } catch (e) { console.warn('[debug] memory snapshot failed:', e?.message); }
  return null;
}

// ─── Send Log to Backend ─────────────────────────────────
function sendLog(entry) {
  const token = secureStorage.getToken();
  entry.breadcrumbs = [...breadcrumbs];
  entry.timestamp = entry.timestamp || new Date().toISOString();
  entry.pageUrl = window.location.pathname + window.location.search;
  entry.userAgent = navigator.userAgent;
  entry.screenSize = `${window.innerWidth}x${window.innerHeight}`;
  entry.memory = getMemorySnapshot();
  
  // Always save to localStorage first (Safe Mode)
  queueToLocal(entry);
  
  if (!token) return;
  
  // Fire-and-forget POST
  axios.post(`${API}/debug/log`, entry, {
    headers: { Authorization: `Bearer ${token}` },
    timeout: 5000
  }).then(() => {
    try {
      const raw = localStorage.getItem(LOCAL_STORAGE_KEY);
      if (raw) {
        const q = JSON.parse(raw);
        const idx = q.findIndex(e => e.timestamp === entry.timestamp);
        if (idx >= 0) q.splice(idx, 1);
        if (q.length) localStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify(q));
        else localStorage.removeItem(LOCAL_STORAGE_KEY);
      }
    } catch (e) { console.warn('[debug] dequeue failed:', e?.message); }
  }).catch(() => {});
}

// Dedupe: don't send the same error repeatedly
const recentHashes = new Set();
function isDuplicate(key) {
  if (recentHashes.has(key)) return true;
  recentHashes.add(key);
  if (recentHashes.size > 100) {
    const first = recentHashes.values().next().value;
    recentHashes.delete(first);
  }
  // Auto-clear after 60s
  setTimeout(() => recentHashes.delete(key), 60000);
  return false;
}

// ─── Axios Interceptor (API Failures + Slow + Large + Stale) ─
const inflightRequests = {}; // url -> {seq, startTime}
let requestSeq = 0;

function installAxiosInterceptor() {
  // Request interceptor
  axios.interceptors.request.use(config => {
    config._debugStart = Date.now();
    config._debugUrl = config.url;
    
    // Track in-flight requests for stale detection
    const url = config.url || '';
    if (url.includes('/search') || url.includes('/filter') || url.includes('/query') || url.includes('/autocomplete')) {
      requestSeq++;
      config._debugSeq = requestSeq;
      const baseUrl = url.split('?')[0];
      inflightRequests[baseUrl] = { seq: requestSeq, startTime: Date.now() };
    }
    
    return config;
  }, error => Promise.reject(error));
  
  // Response interceptor
  axios.interceptors.response.use(
    response => {
      const elapsed = Date.now() - (response.config._debugStart || Date.now());
      const url = response.config._debugUrl || response.config.url || '';
      
      // Skip debug endpoints
      if (url.includes('/debug/')) return response;
      
      // ── Stale response detection ──
      if (response.config._debugSeq) {
        const baseUrl = url.split('?')[0];
        const current = inflightRequests[baseUrl];
        if (current && current.seq > response.config._debugSeq) {
          const key = `stale:${baseUrl}`;
          if (!isDuplicate(key)) {
            sendLog({
              type: 'STALE_RESPONSE',
              severity: 'warning',
              message: `Stale search result: ${baseUrl} (seq ${response.config._debugSeq} arrived after ${current.seq})`,
              details: {
                url: baseUrl,
                query: url.split('?')[1]?.slice(0, 200),
                this_seq: response.config._debugSeq,
                latest_seq: current.seq,
                elapsed_ms: elapsed
              }
            });
          }
        }
      }
      
      // ── Slow API ──
      if (elapsed > SLOW_API_THRESHOLD_MS) {
        const payloadSize = JSON.stringify(response.data || '').length;
        const key = `slow:${url.split('?')[0]}`;
        if (!isDuplicate(key)) {
          sendLog({
            type: 'SLOW_API',
            severity: elapsed > 8000 ? 'error' : 'warning',
            message: `Slow API ${(elapsed / 1000).toFixed(1)}s: ${response.config.method?.toUpperCase()} ${url.split('?')[0]}`,
            details: {
              method: response.config.method,
              url,
              elapsed_ms: elapsed,
              status: response.status,
              payload_bytes: payloadSize,
              payload_readable: payloadSize > 1048576 ? `${(payloadSize / 1048576).toFixed(1)}MB` : payloadSize > 1024 ? `${(payloadSize / 1024).toFixed(0)}KB` : `${payloadSize}B`,
              items_count: Array.isArray(response.data) ? response.data.length : Array.isArray(response.data?.data) ? response.data.data.length : null
            }
          });
        }
      }
      
      // ── Large payload ──
      const dataStr = JSON.stringify(response.data || '');
      if (dataStr.length > LARGE_PAYLOAD_BYTES) {
        const key = `large:${url.split('?')[0]}`;
        if (!isDuplicate(key)) {
          sendLog({
            type: 'LARGE_PAYLOAD',
            severity: 'warning',
            message: `Large response ${(dataStr.length / 1048576).toFixed(1)}MB: ${url.split('?')[0]}`,
            details: {
              url,
              payload_bytes: dataStr.length,
              elapsed_ms: elapsed,
              items_count: Array.isArray(response.data) ? response.data.length : null
            }
          });
        }
      }
      
      return response;
    },
    error => {
      const config = error.config || {};
      const url = config._debugUrl || config.url || 'unknown';
      
      if (url.includes('/debug/')) return Promise.reject(error);
      
      const elapsed = Date.now() - (config._debugStart || Date.now());
      const status = error.response?.status || 0;
      const isTimeout = error.code === 'ECONNABORTED' || error.message?.includes('timeout');
      const isNetworkError = !error.response && !isTimeout;
      
      const key = `err:${status}:${url.split('?')[0]}`;
      if (!isDuplicate(key)) {
        sendLog({
          type: 'API_ERROR',
          severity: status >= 500 ? 'critical' : 'error',
          message: isTimeout
            ? `API Timeout ${(elapsed / 1000).toFixed(1)}s: ${config.method?.toUpperCase()} ${url}`
            : isNetworkError
            ? `Network Error: ${config.method?.toUpperCase()} ${url}`
            : `API ${status}: ${config.method?.toUpperCase()} ${url}`,
          details: {
            method: config.method,
            url,
            status,
            elapsed_ms: elapsed,
            error_message: error.message,
            response_data: error.response?.data ? JSON.stringify(error.response.data).slice(0, 500) : null,
            request_data: config.data ? JSON.stringify(config.data).slice(0, 300) : null,
            is_timeout: isTimeout,
            is_network_error: isNetworkError
          }
        });
      }
      
      return Promise.reject(error);
    }
  );
}

// ─── Global Error Handlers ───────────────────────────────
function installGlobalHandlers() {
  window.addEventListener('error', event => {
    // Skip duplicate resource errors (handled by image tracker)
    if (event.target && event.target !== window) return;
    
    const key = `crash:${event.message}:${event.lineno}`;
    if (isDuplicate(key)) return;
    
    sendLog({
      type: 'JS_CRASH',
      severity: 'critical',
      message: `JS Crash: ${event.message}`,
      details: {
        filename: event.filename,
        lineno: event.lineno,
        colno: event.colno,
        stack: event.error?.stack?.slice(0, 1500)
      }
    });
  });
  
  window.addEventListener('unhandledrejection', event => {
    const reason = event.reason;
    const message = reason instanceof Error ? reason.message : String(reason);
    
    if (reason?.isAxiosError) return; // Already caught by interceptor
    
    const key = `rejection:${message.slice(0, 50)}`;
    if (isDuplicate(key)) return;
    
    sendLog({
      type: 'PROMISE_REJECTION',
      severity: 'error',
      message: `Unhandled Promise: ${message.slice(0, 200)}`,
      details: {
        stack: reason?.stack?.slice(0, 1500),
        reason: String(reason).slice(0, 500)
      }
    });
  });
}

// ─── Image Load Failure Tracker ──────────────────────────
function installImageTracker() {
  // Catch all image load errors via capture phase
  document.addEventListener('error', event => {
    const el = event.target;
    if (el.tagName !== 'IMG') return;
    
    const src = el.src || el.getAttribute('data-src') || '';
    if (!src || src.startsWith('data:')) return; // Skip data URIs
    
    const key = `img:${src.slice(-80)}`;
    if (isDuplicate(key)) return;
    
    // Check context — what page, what component
    const container = el.closest('[data-testid]');
    const testId = container?.getAttribute('data-testid') || '';
    const alt = el.alt || '';
    const classes = el.className || '';
    const parentClasses = el.parentElement?.className || '';
    
    sendLog({
      type: 'IMAGE_FAILED',
      severity: 'error',
      message: `Image failed: ${src.split('/').pop()?.slice(0, 60) || src.slice(-60)}`,
      details: {
        src: src.slice(0, 300),
        alt,
        container: testId,
        element_class: classes.slice(0, 100),
        parent_class: parentClasses.slice(0, 100),
        natural_size: `${el.naturalWidth}x${el.naturalHeight}`,
        display_size: `${el.width}x${el.height}`,
        in_viewport: isInViewport(el)
      }
    });
  }, true); // capture phase to catch before bubbling stops
  
  // ── Periodic viewport image audit ──
  // Detect images in viewport that are still broken/loading after 5s
  setInterval(() => {
    const images = document.querySelectorAll('img');
    let brokenInView = 0;
    const brokenSrcs = [];
    
    images.forEach(img => {
      if (!isInViewport(img)) return;
      if (img.src && !img.src.startsWith('data:') && img.complete && img.naturalWidth === 0) {
        brokenInView++;
        if (brokenSrcs.length < 5) brokenSrcs.push(img.src.slice(-80));
      }
    });
    
    if (brokenInView >= 3) {
      const key = `img-audit:${window.location.pathname}:${brokenInView}`;
      if (!isDuplicate(key)) {
        sendLog({
          type: 'IMAGE_AUDIT',
          severity: 'warning',
          message: `${brokenInView} broken images visible on ${window.location.pathname}`,
          details: {
            broken_count: brokenInView,
            total_images: images.length,
            sample_srcs: brokenSrcs,
            visible_images: Array.from(images).filter(i => isInViewport(i)).length
          }
        });
      }
    }
  }, 60000); // Every 60 seconds — reduced from 10s
}

function isInViewport(el) {
  try {
    const rect = el.getBoundingClientRect();
    return rect.top < window.innerHeight && rect.bottom > 0 && rect.left < window.innerWidth && rect.right > 0;
  } catch (_) { return false; }
}

// ─── Long Task / UI Freeze Detector ─────────────────────
function installFreezeDetector() {
  // PerformanceObserver for Long Tasks (>50ms main thread block)
  if (typeof PerformanceObserver !== 'undefined') {
    try {
      let longTaskCount = 0;
      let totalBlockedMs = 0;
      
      const observer = new PerformanceObserver(list => {
        for (const entry of list.getEntries()) {
          longTaskCount++;
          totalBlockedMs += entry.duration;
          
          // Only log really bad freezes (>200ms)
          if (entry.duration > 200) {
            const key = `freeze:${window.location.pathname}:${Math.round(entry.startTime / 1000)}`;
            if (!isDuplicate(key)) {
              sendLog({
                type: 'UI_FREEZE',
                severity: entry.duration > 1000 ? 'critical' : entry.duration > 500 ? 'error' : 'warning',
                message: `UI froze ${Math.round(entry.duration)}ms on ${window.location.pathname}`,
                details: {
                  duration_ms: Math.round(entry.duration),
                  start_time: Math.round(entry.startTime),
                  long_tasks_total: longTaskCount,
                  total_blocked_ms: Math.round(totalBlockedMs)
                }
              });
            }
          }
        }
      });
      observer.observe({ type: 'longtask', buffered: true });
    } catch (e) { console.warn('[debug] longtask observer failed:', e?.message); }
  }
  
  // Fallback: heartbeat-based freeze detection
  // If the heartbeat gap exceeds 3 seconds, the main thread was blocked
  let lastHeartbeat = Date.now();
  setInterval(() => {
    const now = Date.now();
    const gap = now - lastHeartbeat;
    lastHeartbeat = now;
    
    if (gap > 3000) {
      const key = `heartbeat-freeze:${window.location.pathname}:${Math.round(now / 5000)}`;
      if (!isDuplicate(key)) {
        sendLog({
          type: 'UI_FREEZE',
          severity: gap > 8000 ? 'critical' : 'error',
          message: `App unresponsive ${(gap / 1000).toFixed(1)}s on ${window.location.pathname}`,
          details: {
            gap_ms: gap,
            likely_cause: 'Main thread blocked — heavy computation or massive DOM render'
          }
        });
      }
    }
  }, 1000);
}

// ─── Page Load Performance Tracker ───────────────────────
function installPageLoadTracker() {
  // Track route changes and measure time-to-interactive
  let routeStartTime = null;
  let routePath = null;
  
  const origPushState = history.pushState;
  history.pushState = function(...args) {
    // Log previous route's load time if we have data
    if (routeStartTime && routePath) {
      const loadTime = Date.now() - routeStartTime;
      if (loadTime > 5000) {
        sendLog({
          type: 'SLOW_PAGE_LOAD',
          severity: loadTime > 10000 ? 'error' : 'warning',
          message: `Page load ${(loadTime / 1000).toFixed(1)}s: ${routePath}`,
          details: {
            route: routePath,
            load_ms: loadTime,
            dom_nodes: document.querySelectorAll('*').length
          }
        });
      }
    }
    
    routeStartTime = Date.now();
    routePath = args[2] || window.location.pathname;
    addBreadcrumb('navigate', routePath);
    
    origPushState.apply(this, args);
  };
  
  window.addEventListener('popstate', () => {
    routeStartTime = Date.now();
    routePath = window.location.pathname;
    addBreadcrumb('navigate', routePath);
  });
}

// ─── Click & Input Breadcrumbs ───────────────────────────
function installBreadcrumbTrackers() {
  document.addEventListener('click', event => {
    const el = event.target.closest('button, a, [role="button"], [data-testid], input, select, [role="tab"], [role="menuitem"]');
    if (!el) return;
    const testId = el.getAttribute('data-testid') || '';
    const text = (el.textContent || '').trim().slice(0, 50);
    const tag = el.tagName.toLowerCase();
    addBreadcrumb('click', testId || `${tag}: ${text}`);
  }, { passive: true, capture: true });
  
  // Track search/filter input with debounce
  let inputDebounce = null;
  document.addEventListener('input', event => {
    const el = event.target;
    if (el.tagName !== 'INPUT' && el.tagName !== 'TEXTAREA') return;
    clearTimeout(inputDebounce);
    inputDebounce = setTimeout(() => {
      const placeholder = el.placeholder || el.name || el.id || '';
      const val = el.value?.slice(0, 30) || '';
      if (placeholder.toLowerCase().includes('search') || placeholder.toLowerCase().includes('filter')) {
        addBreadcrumb('search', `"${val}" in ${placeholder}`);
      }
    }, 500);
  }, { passive: true, capture: true });
}

// ─── Scroll Depth Tracker (for lazy load diagnosis) ──────
function installScrollTracker() {
  let maxScrollPct = 0;
  let scrollPage = window.location.pathname;
  
  const handler = () => {
    if (window.location.pathname !== scrollPage) {
      maxScrollPct = 0;
      scrollPage = window.location.pathname;
    }
    const scrollTop = window.scrollY || document.documentElement.scrollTop;
    const docHeight = document.documentElement.scrollHeight - window.innerHeight;
    if (docHeight > 0) {
      const pct = Math.round((scrollTop / docHeight) * 100);
      if (pct > maxScrollPct) maxScrollPct = pct;
    }
  };
  
  window.addEventListener('scroll', handler, { passive: true });
  
  // Attach maxScrollPct to breadcrumbs periodically
  setInterval(() => {
    if (maxScrollPct > 50) {
      addBreadcrumb('scroll', `${maxScrollPct}% on ${scrollPage}`);
      maxScrollPct = 0;
    }
  }, 15000);
}

// ─── Empty State Detection (export for components) ───────
export function logEmptyState(componentName, apiUrl, responseData) {
  const hasData = responseData && (
    (Array.isArray(responseData) && responseData.length > 0) ||
    (typeof responseData === 'object' && Object.keys(responseData).length > 0)
  );
  
  if (hasData) {
    sendLog({
      type: 'EMPTY_STATE_MISMATCH',
      severity: 'error',
      message: `${componentName} shows empty but API returned data`,
      details: {
        component: componentName,
        api_url: apiUrl,
        data_type: Array.isArray(responseData) ? `Array[${responseData.length}]` : typeof responseData,
        data_sample: JSON.stringify(responseData).slice(0, 300)
      }
    });
  }
}

// ─── Component Crash Logger (for ErrorBoundary) ──────────
export function logComponentCrash(error, errorInfo, componentName) {
  sendLog({
    type: 'COMPONENT_CRASH',
    severity: 'critical',
    message: `Component Crash: ${error?.message || 'Unknown'}`,
    details: {
      component: componentName || 'Unknown',
      error: error?.toString().slice(0, 500),
      stack: error?.stack?.slice(0, 1500),
      component_stack: errorInfo?.componentStack?.slice(0, 1500),
      dom_nodes: document.querySelectorAll('*').length
    }
  });
}

// ─── Initialize Everything ───────────────────────────────
export function initDebugTracker() {
  if (isInitialized) return;
  isInitialized = true;
  
  installAxiosInterceptor();
  installGlobalHandlers();
  installBreadcrumbTrackers();
  installImageTracker();
  installFreezeDetector();
  installPageLoadTracker();
  installScrollTracker();
  
  // Flush local queue on load (Safe Mode recovery)
  setTimeout(flushLocalQueue, 3000);
  
  // Periodic flush
  flushTimer = setInterval(flushLocalQueue, FLUSH_INTERVAL_MS);
  
  // Flush before page unload
  window.addEventListener('beforeunload', () => {
    if (flushTimer) clearInterval(flushTimer);
    flushLocalQueue();
  });
  
  addBreadcrumb('init', `Debug tracker v2 | ${navigator.userAgent.slice(0, 50)}`);
}

export default { initDebugTracker, logEmptyState, logComponentCrash, addBreadcrumb };
